import { useState } from 'react';
import { Warehouse, Lock, Unlock, ArrowRightLeft, History, Package, Edit3 } from 'lucide-react';
import { formatDate } from '../components/StockIndicator';

/**
 * StokTablosu — Stok sekmesi içeriği
 * 
 * @param {Array} stokData - Stok havuzu verisi [{altTip, toplamStok, ayrilmisStok, serbestStok, ...}]
 * @param {Array} hareketData - Hareket geçmişi [{hareketTipi, miktar, tarih, ...}]
 * @param {Object} hareketTipleri - Hareket tipi meta verisi
 * @param {string} birim - Birim (ton, kutu, m² vb.)
 * @param {Function} onAktarim - Aktarım butonu tıklaması
 * @param {Function} onDuzeltme - Manuel düzeltme butonu tıklaması
 */
export default function StokTablosu({ stokData = [], hareketData = [], hareketTipleri = {}, birim = 'adet', onAktarim, onDuzeltme }) {
    const [showHareketler, setShowHareketler] = useState(false);

    const toplamStok = stokData.reduce((acc, s) => acc + s.toplamStok, 0);
    const ayrilmisStok = stokData.reduce((acc, s) => acc + s.ayrilmisStok, 0);
    const serbestStok = stokData.reduce((acc, s) => acc + s.serbestStok, 0);

    return (
        <div className="space-y-4 p-4">
            {/* Özet Kartları */}
            <div className="grid grid-cols-3 gap-4">
                <div className="bg-bg-tertiary rounded-xl p-4 border border-border-light">
                    <div className="flex items-center gap-2 text-text-muted mb-1">
                        <Package size={16} />
                        <span className="text-xs font-medium">Toplam Stok</span>
                    </div>
                    <p className="text-2xl font-bold text-text-primary">{toplamStok} <span className="text-sm font-normal text-text-muted">{birim}</span></p>
                </div>
                <div className="bg-bg-tertiary rounded-xl p-4 border border-border-light">
                    <div className="flex items-center gap-2 text-warning mb-1">
                        <Lock size={16} />
                        <span className="text-xs font-medium">Ayrılmış</span>
                    </div>
                    <p className="text-2xl font-bold text-warning">{ayrilmisStok} <span className="text-sm font-normal text-text-muted">{birim}</span></p>
                </div>
                <div className="bg-bg-tertiary rounded-xl p-4 border border-border-light">
                    <div className="flex items-center gap-2 text-success mb-1">
                        <Unlock size={16} />
                        <span className="text-xs font-medium">Serbest</span>
                    </div>
                    <p className="text-2xl font-bold text-success">{serbestStok} <span className="text-sm font-normal text-text-muted">{birim}</span></p>
                </div>
            </div>

            {/* Aksiyon Butonları */}
            <div className="flex gap-2">
                {onAktarim && (
                    <button onClick={onAktarim} className="flex items-center gap-2 px-3 py-2 bg-info/10 text-info rounded-lg text-sm font-medium hover:bg-info/20 transition-colors">
                        <ArrowRightLeft size={16} />
                        İnşaata Aktar
                    </button>
                )}
                {onDuzeltme && (
                    <button onClick={onDuzeltme} className="flex items-center gap-2 px-3 py-2 bg-warning/10 text-warning rounded-lg text-sm font-medium hover:bg-warning/20 transition-colors">
                        <Edit3 size={16} />
                        Manuel Düzeltme
                    </button>
                )}
                <button
                    onClick={() => setShowHareketler(!showHareketler)}
                    className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary text-text-secondary rounded-lg text-sm font-medium hover:bg-bg-hover transition-colors ml-auto"
                >
                    <History size={16} />
                    {showHareketler ? 'Geçmişi Gizle' : 'Hareket Geçmişi'}
                </button>
            </div>

            {/* Alt Tip Bazlı Stok Tablosu */}
            <div className="bg-bg-secondary rounded-xl border border-border-light overflow-hidden">
                <table className="w-full">
                    <thead className="bg-bg-tertiary">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Alt Tip</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Toplam</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Ayrılmış</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Serbest</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Son Giriş</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">Depo</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border-light">
                        {stokData.map(item => (
                            <tr key={item.id} className="hover:bg-bg-hover transition-colors">
                                <td className="px-4 py-3">
                                    <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium">{item.altTip}</span>
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-text-primary">{item.toplamStok} {birim}</td>
                                <td className="px-4 py-3 text-sm text-warning font-medium">{item.ayrilmisStok} {birim}</td>
                                <td className="px-4 py-3 text-sm text-success font-semibold">{item.serbestStok} {birim}</td>
                                <td className="px-4 py-3 text-sm text-text-muted">{item.sonGirisTarihi ? formatDate(item.sonGirisTarihi) : '—'}</td>
                                <td className="px-4 py-3 text-sm text-text-secondary">{item.depoKonum}</td>
                            </tr>
                        ))}
                        {stokData.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-8 text-center text-text-muted">Stok verisi bulunamadı</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Hareket Geçmişi */}
            {showHareketler && hareketData.length > 0 && (
                <div className="bg-bg-secondary rounded-xl border border-border-light overflow-hidden">
                    <div className="px-4 py-3 border-b border-border-light bg-bg-tertiary">
                        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                            <History size={16} className="text-text-muted" />
                            Hareket Geçmişi
                        </h3>
                    </div>
                    <div className="divide-y divide-border-light max-h-80 overflow-y-auto">
                        {hareketData.map(h => {
                            const tip = hareketTipleri[h.hareketTipi] || { label: h.hareketTipi, color: 'neutral', icon: '•' };
                            return (
                                <div key={h.id} className="px-4 py-3 flex items-center gap-3 hover:bg-bg-hover transition-colors">
                                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm bg-${tip.color}/10 text-${tip.color}`}>
                                        {tip.icon}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium bg-${tip.color}/10 text-${tip.color}`}>{tip.label}</span>
                                            <span className="text-xs text-text-muted">{h.altTip}</span>
                                        </div>
                                        <p className="text-sm text-text-secondary truncate">{h.aciklama}</p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className={`text-sm font-semibold ${h.hareketTipi === 'giris' ? 'text-success' : h.hareketTipi === 'cikis' ? 'text-error' : 'text-info'}`}>
                                            {h.hareketTipi === 'giris' ? '+' : '-'}{h.miktar}
                                        </p>
                                        <p className="text-xs text-text-muted">{formatDate(h.tarih)}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
